import { useProgress } from "@react-three/drei";
import { useEffect, useRef, useState } from "react";
import { CompanyLogo } from "../brand/CompanyLogo.js";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";
import { useWorldT } from "./i18n.js";
import { loadProgress, loadedSteps, useLoadVersion } from "./loading.js";
import { useWorldState } from "./store.js";
import type { WorldStore } from "./store.js";

const MAX_MS = 20_000;
const FADE_MS = 450;

/**
 * Shown over the canvas until the scene code, the world data, the assets and the first frame are
 * in: the company's logo on its brand colour (the Uzverse mark without branding) and a progress
 * bar. Fades out, then leaves the DOM. A hard time limit keeps it from ever blocking the view.
 */
export function LoadingScreen({ store }: { store: WorldStore }) {
  const t = useWorldT();
  const { services } = useSession();
  const org = useLoad(() => services.api.listOrganization());
  const company = org.data?.[0];
  const { status } = useWorldState(store);
  const assets = useProgress();
  useLoadVersion();
  const steps = loadedSteps();
  const peak = useRef(0);
  const [gone, setGone] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const data = status !== "loading";
  const raw = loadProgress({
    chunk: steps.chunk,
    data,
    assets: assets.active ? assets.progress / 100 : 1,
    frame: steps.frame,
  });
  peak.current = Math.max(peak.current, raw);
  const ready = steps.chunk && data && steps.frame && !assets.active;

  useEffect(() => {
    if (!ready) return;
    setLeaving(true);
    const id = setTimeout(() => setGone(true), FADE_MS);
    return () => clearTimeout(id);
  }, [ready]);
  useEffect(() => {
    const id = setTimeout(() => setGone(true), MAX_MS);
    return () => clearTimeout(id);
  }, []);
  // An unavailable world shows its own message with a retry; the loader must not hide it.
  if (gone || status === "unavailable") return null;

  const stage = !steps.chunk
    ? t("game.loadingScene")
    : !data
      ? t("game.loadingData")
      : assets.active
        ? t("game.loadingAssets")
        : steps.frame
          ? t("game.loadingReady")
          : t("game.loadingScene");
  const pct = Math.round(peak.current * 100);
  const brand = company?.branding?.colors.primary;
  return (
    <div
      className={`world-loading${leaving ? " is-leaving" : ""}`}
      data-testid="world-loading"
      role="status"
      aria-live="polite"
      style={brand ? ({ "--brand": brand } as React.CSSProperties) : undefined}
    >
      <div className="world-loading-card">
        <div className="world-loading-logo" aria-hidden="true">
          {company ? (
            <CompanyLogo name={company.name} branding={company.branding} size={72} />
          ) : (
            <span className="world-loading-mark">U</span>
          )}
        </div>
        <h2>{company?.name ?? "Uzverse"}</h2>
        <p>{t("game.loadingTitle")}</p>
        <div
          className="world-loading-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
        <small>
          {stage} · {pct}%
        </small>
      </div>
    </div>
  );
}
